import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DDNav } from "../../../components/Admin/DDNav/DDNav";
import { DDNextBar } from "../../../components/Admin/DDNextBar";
import { DDLoadError } from "../../../components/Admin/DDLoadError";
import { ConfirmModal } from "../../../components/Admin/ConfirmModal/ConfirmModal";
import { DDReportCard } from "../../../components/Admin/DDReportCard";
import { useAuth } from "../../../hooks";
import { DdFla } from "../../../api";
import { FLA_CODE, FLA_ST, FLA_CAP_STATUS, FLA_TOTAL_BM } from "../../../utils/fla/flaCatalog";
import {
  flaDefaultDB, flaElemScore, flaGlobalScore,
  flaGetGaps, flaTabDot, flaExportJSON, flaParseImport,
} from "../../../utils/fla/flaStorage";
import "./FlaAssessment.scss";

// El generador del informe y sus diccionarios (ES/EN/PT) se cargan recién al generarlo.
const buildFlaReport = (lang, factory) =>
  import("../../../utils/ddReports/flaReport").then((m) => m.buildFlaReportHTML(lang, factory));

const flaReportFileName = (lang, factory) => {
  const nm = (factory?.SETUP?.name || "fabrica").replace(/[^a-z0-9]/gi, "_");
  return "Sustemia_FLA_Informe_" + nm + "_" + lang.toUpperCase() + "_" + new Date().toISOString().slice(0, 10) + ".html";
};

const ddFlaApi = new DdFla();

const VIEWS = ["setup", "guide", "assess", "gap", "cap", "report"];

export function FlaAssessment() {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [db, setDb]               = useState(null);
  const [view, setView]           = useState(() => {
    const v = new URLSearchParams(location.search).get("v");
    return VIEWS.includes(v) ? v : "setup";
  });
  const [currentElem, setCurrentElem] = useState("ER");
  const [openCaps, setOpenCaps]   = useState({});
  const [confirm, setConfirm] = useState({ open: false, msg: "", onConfirm: null });
  const [facMenuOpen, setFacMenuOpen] = useState(false);
  const [setupPanelOpen, setSetupPanelOpen] = useState(false);
  const [guidePanelOpen, setGuidePanelOpen] = useState(true);
  const [faqOpen, setFaqOpen] = useState({});
  const fileInputRef = useRef(null);

  // Sincronizar vista con el parámetro ?v= de la URL
  useEffect(() => {
    const v = new URLSearchParams(location.search).get("v");
    setView(VIEWS.includes(v) ? v : "setup");
  }, [location.search]);

  // Cargar desde API al montar
  // Si la carga falla NO se usa la fábrica de demostración: el primer cambio la guardaría
  // encima de la evaluación real de la empresa.
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!accessToken) return;
    setLoadError(false);
    ddFlaApi.getData(accessToken)
      .then(d => {
        if (d?.factories && Object.keys(d.factories).length > 0) setDb(d);
        else setDb(flaDefaultDB());
      })
      .catch((err) => { console.error("Error cargando Autoevaluación FLA", err); setLoadError(true); });
  }, [accessToken, reloadKey]);

  const persist = useCallback((nextDb) => {
    setDb({ ...nextDb });
    if (accessToken) ddFlaApi.saveData(accessToken, nextDb).catch(() => {});
  }, [accessToken]);

  const goStage = (v) => navigate(`/admin/fla-assessment${v === "setup" ? "" : `?v=${v}`}`);

  if (loadError) return <DDLoadError className="fla-page" onRetry={() => setReloadKey((k) => k + 1)} />;
  if (!db) return <div className="fla-page"><DDNav /><div className="fla-loading">Cargando…</div></div>;

  const cur      = db.factories[db.current];
  const ratings  = cur.RATINGS  || {};
  const notes    = cur.NOTES    || {};
  const capdata  = cur.CAPDATA  || {};
  const setup    = cur.SETUP    || {};

  // ── helpers ─────────────────────────────────────────────────────────────

  const setRating = (code, val) => {
    const next = { ...db };
    const R = { ...next.factories[db.current].RATINGS };
    if (R[code] === val) delete R[code]; else R[code] = val;
    next.factories[db.current].RATINGS = R;
    persist(next);
  };

  const setNote = (code, val) => {
    const next = { ...db };
    next.factories[db.current].NOTES = { ...notes, [code]: val };
    persist(next);
  };

  const setSetup = (key, val) => {
    const next = { ...db };
    next.factories[db.current].SETUP = { ...setup, [key]: val };
    persist(next);
  };

  const setAllElem = (val) => {
    const next = { ...db };
    const R = { ...ratings };
    FLA_CODE.find(e => e.id === currentElem).bm.forEach(b => { R[b.c] = val; });
    next.factories[db.current].RATINGS = R;
    persist(next);
  };

  const clearElem = () => {
    const next = { ...db };
    const R = { ...ratings };
    FLA_CODE.find(e => e.id === currentElem).bm.forEach(b => { delete R[b.c]; });
    next.factories[db.current].RATINGS = R;
    persist(next);
  };

  const selectFactory = (id) => {
    persist({ ...db, current: id });
    setFacMenuOpen(false);
    goStage("setup");
  };

  const createBlankFactory = () => {
    const id = "fac_" + Date.now();
    const next = { ...db };
    next.factories[id] = { id, flag: "🏭", SETUP: {}, RATINGS: {}, NOTES: {}, CAPDATA: {} };
    next.current = id;
    persist(next);
    setFacMenuOpen(false);
    goStage("setup");
  };

  const deleteFactoryById = (id) => {
    if (Object.keys(db.factories).length <= 1) { alert("Debe quedar al menos una fábrica."); return; }
    setConfirm({
      open: true,
      msg: `¿Eliminar la fábrica "${db.factories[id]?.SETUP?.name || "(sin nombre)"}" y todos sus datos?`,
      onConfirm: () => {
        const next = { ...db };
        delete next.factories[id];
        if (next.current === id) next.current = Object.keys(next.factories)[0];
        persist(next);
        setConfirm({ open: false, msg: "", onConfirm: null });
      },
    });
  };

  const syncCAP = () => {
    const gaps = flaGetGaps(ratings, notes);
    const next = { ...db };
    const C = { ...capdata };
    Object.keys(C).forEach(k => { if (!gaps.find(g => g.code === k)) delete C[k]; });
    gaps.forEach(g => { if (!C[g.code]) C[g.code] = { root: "", action: "", owner: "", due: "", status: "Abierta" }; });
    next.factories[db.current].CAPDATA = C;
    persist(next);
  };

  const updateCap = (code, field, val) => {
    const next = { ...db };
    const C = { ...capdata };
    if (!C[code]) C[code] = { root: "", action: "", owner: "", due: "", status: "Abierta" };
    C[code] = { ...C[code], [field]: val };
    next.factories[db.current].CAPDATA = C;
    persist(next);
  };

  const toggleCap = (code) => setOpenCaps(prev => ({ ...prev, [code]: !prev[code] }));
  const toggleFaq = (i) => setFaqOpen(prev => ({ ...prev, [i]: !prev[i] }));

  const triggerImport = () => fileInputRef.current?.click();

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let imported;
      try {
        imported = flaParseImport(reader.result);
      } catch (err) {
        alert(err.message || "Archivo inválido.");
        return;
      }
      setConfirm({
        open: true,
        msg: `Se reemplazarán todas las fábricas actuales por las ${Object.keys(imported.factories).length} del archivo importado. ¿Continuar?`,
        onConfirm: () => {
          persist(imported);
          setConfirm({ open: false, msg: "", onConfirm: null });
        },
      });
    };
    reader.readAsText(file);
  };

  // ── computed ─────────────────────────────────────────────────────────────

  const globalScore = flaGlobalScore(ratings);
  const gaps        = flaGetGaps(ratings, notes);
  const facName     = setup.name || "Seleccionar fábrica";

  // ── sub-components ───────────────────────────────────────────────────────

  // Pasos del proceso + fábrica activa (van debajo del título de cada vista)
  function FlaStepper() {
    const allAnswered = globalScore.answered === FLA_TOTAL_BM;
    const closed = Object.values(capdata).filter(c => c.status === "Cerrada" || c.status === "Verificada").length;
    const steps = [
      { v: "assess", lbl: "Evaluar",        sub: "9 elementos",               done: allAnswered },
      { v: "gap",    lbl: "Brechas",        sub: `${gaps.length} detectadas`, done: allAnswered },
      { v: "cap",    lbl: "Plan de acción", sub: `${gaps.length} acciones`,   done: gaps.length > 0 && closed === gaps.length },
      { v: "report", lbl: "Reporte",        sub: "resultado",                 done: false },
    ];
    return (
      <div className="no-print">
        <div className="fla-stepper">
          {steps.map((s, i) => {
            const active = view === s.v;
            return (
              <button key={s.v} className={`fla-fstep${active ? " active" : s.done ? " done" : ""}`} onClick={() => goStage(s.v)}>
                <span className="fla-fs-n">{s.done && !active ? "✓" : i + 1}</span>
                <span className="fla-fs-tx"><b>{s.lbl}</b><small>{s.sub}</small></span>
              </button>
            );
          })}
          <button className={`fla-help-link${view === "guide" ? " active" : ""}`} onClick={() => goStage("guide")}>? ¿Cómo funciona?</button>
        </div>

        <div className="fla-facbar">
          <div className="fla-facbar-l">
            <span className="fla-facbar-lbl">Fábrica</span>
            <FacSwitch />
          </div>
          <button className="fla-btn fla-btn--sm" onClick={() => goStage("setup")}>Datos de la fábrica</button>
        </div>
      </div>
    );
  }

  function FacSwitch() {
    return (
      <div className="fla-topbar">
        <div className="fla-fac-switch">
          <button className="fla-fac-btn" onClick={() => setFacMenuOpen(o => !o)}>
            <span className="fla-fac-ico">🏭</span>{facName}<span className="fla-fac-caret">▾</span>
          </button>
          {facMenuOpen && (
            <div className="fla-fac-menu">
              <div className="fla-fac-menu-h">Fábricas (demo · editables)</div>
              {Object.values(db.factories).map(f => {
                const sc = flaGlobalScore(f.RATINGS || {});
                const col = sc.applicable === 0 ? "muted" : sc.pct >= 90 ? "g" : sc.pct >= 70 ? "y" : sc.pct >= 50 ? "o" : "r";
                const nm = f.SETUP?.name || "(sin nombre)";
                const meta = [f.SETUP?.city, f.SETUP?.brand].filter(Boolean).join(" · ") || "sin datos";
                return (
                  <div key={f.id} className={`fla-fac-item${db.current === f.id ? " fla-fac-item--active" : ""}`} onClick={() => selectFactory(f.id)}>
                    <span className="fla-fi-flag">{f.flag || "🏭"}</span>
                    <span className="fla-fi-txt">
                      <span className="fla-fi-name">{nm}</span>
                      <span className="fla-fi-meta">{meta}</span>
                    </span>
                    <span className={`fla-fi-score fla-pill fla-pill--${col === "muted" ? "muted" : col}`}>{sc.applicable > 0 ? `${sc.pct}%` : "—"}</span>
                    <span className="fla-fi-del" title="Eliminar" onClick={(e) => { e.stopPropagation(); deleteFactoryById(f.id); }}>🗑</span>
                  </div>
                );
              })}
              <div className="fla-fac-menu-sep" />
              <button className="fla-fac-new" onClick={createBlankFactory}>＋ Nueva fábrica en blanco</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function ProgBar() {
    const tot = FLA_TOTAL_BM;
    const g = globalScore;
    return (
      <div className="fla-prog-wrap">
        <div className="fla-prog-bar">
          {g.c  > 0 && <div className="fla-seg fla-seg--c"  style={{ width: `${(g.c  / tot) * 100}%` }} />}
          {g.nc > 0 && <div className="fla-seg fla-seg--nc" style={{ width: `${(g.nc / tot) * 100}%` }} />}
          {g.ue > 0 && <div className="fla-seg fla-seg--ue" style={{ width: `${(g.ue / tot) * 100}%` }} />}
          {g.na > 0 && <div className="fla-seg fla-seg--na" style={{ width: `${(g.na / tot) * 100}%` }} />}
        </div>
        <div className="fla-prog-legend">
          <span><i className="fla-dot fla-dot--g" /> Compliant <b>{g.c}</b></span>
          <span><i className="fla-dot fla-dot--r" /> Non-Compliant <b>{g.nc}</b></span>
          <span><i className="fla-dot fla-dot--y" /> Uncorroborated <b>{g.ue}</b></span>
          <span><i className="fla-dot fla-dot--muted" /> N/A <b>{g.na}</b></span>
          <span><i className="fla-dot fla-dot--line" /> Sin responder <b>{g.pending}</b></span>
        </div>
      </div>
    );
  }

  function ElemGrid({ onNavigate, card }) {
    return (
      <div className={`fla-elem-grid${card ? " fla-elem-grid--card" : ""}`}>
        {FLA_CODE.map(e => {
          const sc = flaElemScore(e, ratings);
          const col = sc.cls === "n" ? "soft" : sc.cls;
          return (
            <div
              key={e.id}
              className={`fla-elem-card fla-elem-card--${sc.cls}`}
              onClick={() => { setCurrentElem(e.id); onNavigate(); }}
            >
              <div className="fla-ec-h">
                <span className="fla-ec-ico">{e.ico}</span>
                <span className="fla-ec-code">Elemento {e.n} · {e.codeStd}</span>
              </div>
              <div className="fla-ec-name">{e.name}</div>
              <div className="fla-ec-bar">
                <i style={{ width: `${sc.applicable > 0 ? sc.pct : 0}%`, background: `var(--fla-${col})` }} />
              </div>
              <div className="fla-ec-meta">
                <span>{sc.answered}/{e.bm.length} respondidos</span>
                <span className="fla-ec-pct" style={{ color: `var(--fla-${col})` }}>{sc.done ? `${sc.pct}%` : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function Pill({ status }) {
    const st = FLA_ST[status];
    return <span className={`fla-pill ${st?.pillCls || "fla-pill--muted"}`}>{st?.lbl || "pendiente"}</span>;
  }

  function MethPanel({ id, title, open, onToggle, children }) {
    return (
      <div className={`fla-mp${open ? " fla-mp--open" : ""}`}>
        <div className="fla-mp-h" onClick={onToggle}>
          <h3>{title}</h3>
          <span className="fla-mp-toggle">▾</span>
        </div>
        {open && <div className="fla-mp-body">{children}</div>}
      </div>
    );
  }

  // ── VIEW: INICIO / SETUP ─────────────────────────────────────────────────

  function ViewSetup() {
    return (
      <div>
        <div className="fla-ph">
          <div>
            <h1 className="fla-ph-title">Autoevaluación · FLA Workplace Code of Conduct</h1>
            <div className="fla-ph-sub">Herramienta de autodiagnóstico para fábricas · 9 elementos del Código y sus Compliance Benchmarks</div>
          </div>
        </div>
        <FlaStepper />

        <MethPanel id="setup" title="📘 Cómo funciona esta autoevaluación · metodología FLA" open={setupPanelOpen} onToggle={() => setSetupPanelOpen(o => !o)}>
          <p>Esta herramienta replica la lógica de verificación de la <b>Fair Labor Association (FLA)</b>. La fábrica se autoevalúa contra los 9 elementos del <b>Workplace Code of Conduct</b>, cada uno desglosado en <i>Compliance Benchmarks</i> (los requisitos verificables que un assessor FLA revisaría en planta).</p>
          <div className="fla-mp-source">El Código y sus benchmarks definen estándares laborales basados en los convenios de la OIT y en buenas prácticas internacionalmente aceptadas, para alcanzar condiciones de trabajo dignas y humanas en las fábricas.
            <span className="fla-mp-source-src">— FLA Workplace Code of Conduct and Compliance Benchmarks (fairlabor.org)</span>
          </div>
          <h5>Cómo calificás cada benchmark</h5>
          <p>Para cada ítem la fábrica selecciona un estado, siguiendo el esquema de calificación de hallazgos que usa la FLA en sus assessments:</p>
          <p style={{ margin: "4px 0 0" }}>
            <span className="fla-pill fla-pill--g">Compliant</span> cumple plenamente · <span className="fla-pill fla-pill--r">Non-Compliant</span> incumplimiento identificado · <span className="fla-pill fla-pill--y">Uncorroborated</span> sin evidencia suficiente para confirmar cumplimiento · <span className="fla-pill fla-pill--muted">N/A</span> no aplica a esta operación
          </p>
          <h5>De la autoevaluación al plan de acción</h5>
          <ol className="fla-mp-steps">
            <li><b>Autoevaluación:</b> respondé los benchmarks de los 9 elementos y adjuntá la evidencia / nota de soporte.</li>
            <li><b>Gap Analysis:</b> el sistema consolida automáticamente todo lo marcado como <i>Non-Compliant</i> o <i>Uncorroborated</i> como una brecha (gap).</li>
            <li><b>Plan de Acción (CAP):</b> cada brecha genera una línea de Corrective Action Plan — acción, responsable, plazo y estado de remediación.</li>
            <li><b>Reporte:</b> dashboard de scoring por elemento + reporte imprimible/exportable para compartir con la marca o el verificador.</li>
          </ol>
          <div className="fla-mp-source fla-mp-source--warn">Esta es una herramienta de <b>autodiagnóstico y preparación</b>. No sustituye una verificación independiente acreditada de la FLA ni constituye certificación alguna.
            <span className="fla-mp-source-src">— Nota de alcance · Sustemia</span>
          </div>
        </MethPanel>

        <div className="fla-card">
          <h3 className="fla-card-hd">Datos de la fábrica</h3>
          <div className="fla-card-s">se usa en el encabezado del reporte</div>
          <div className="fla-setup-grid">
            {[
              ["name","Nombre de la fábrica","Razón social / nombre comercial","text"],
              ["country","País","País de operación","text"],
              ["city","Ciudad / Región","Localidad","text"],
              ["brand","Marca(s) cliente","Ej. PUMA, Nike, Ralph Lauren","text"],
              ["workers","N° de trabajadores","Total dotación","text"],
              ["product","Producto principal","Apparel / calzado / accesorios","text"],
              ["resp","Responsable de la autoevaluación","Nombre y cargo","text"],
              ["date","Fecha de autoevaluación","","date"],
              ["period","Período / ciclo","Ej. FY2026 / Q2-2026","text"],
            ].map(([k, lbl, ph, type]) => (
              <div key={k} className="fla-fld">
                <label>{lbl}</label>
                <input type={type} value={setup[k] || ""} placeholder={ph} onChange={e => setSetup(k, e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <div className="fla-note fla-note--info">
          <span className="fla-note-ic">→</span>
          <div>Cuando termines de cargar los datos, pasá a <b>Autoevaluación</b> para responder los 9 elementos del Código. Tu progreso se guarda automáticamente.</div>
        </div>

        <ElemGrid onNavigate={() => goStage("assess")} />
      </div>
    );
  }

  // ── VIEW: AUTOEVALUACIÓN ─────────────────────────────────────────────────

  function ViewAssess() {
    const elem = FLA_CODE.find(e => e.id === currentElem);
    return (
      <div>
        <div className="fla-ph">
          <div>
            <h1 className="fla-ph-title">Autoevaluación FLA</h1>
            <div className="fla-ph-sub">Calificá cada criterio de los 9 elementos del Código.</div>
          </div>
          <div className="fla-ph-r">
            <button className="fla-btn fla-btn--sm" onClick={() => setAllElem("compliant")}>Marcar todo Compliant</button>
            <button className="fla-btn fla-btn--sm" onClick={clearElem}>Limpiar elemento</button>
          </div>
        </div>
        <FlaStepper />

        <ProgBar />

        {/* Tabs de elementos */}
        <div className="fla-tabs">
          {FLA_CODE.map(e => (
            <button
              key={e.id}
              className={`fla-tab${e.id === currentElem ? " fla-tab--active" : ""}`}
              onClick={() => setCurrentElem(e.id)}
            >
              <span className={`fla-tdot fla-tdot--${flaTabDot(e, ratings)}`} />
              <span className="fla-tnum">{e.n}</span>
              {e.codeStd}
            </button>
          ))}
        </div>

        {/* Benchmarks del elemento activo */}
        <div className="fla-bm-intro">
          <span className="fla-bm-intro-ico">{elem.ico}</span>
          <div>
            <div className="fla-code">ELEMENTO {elem.n} · {elem.codeStd}</div>
            <h2 className="fla-bm-intro-name">{elem.name}</h2>
            <p className="fla-bm-intro-def">{elem.intro}</p>
            <div className="fla-bm-intro-src">{elem.src}</div>
          </div>
        </div>

        {elem.bm.map(b => {
          const r = ratings[b.c] || "";
          const isGap = r === "nc" || r === "ue";
          return (
            <div key={b.c} className="fla-bm">
              <div className="fla-bm-head">
                <span className="fla-bm-code">{b.c}</span>
                <div className="fla-bm-txt">
                  <div className="fla-bm-title">{b.t}</div>
                  <div className="fla-bm-bench">{b.b}</div>
                </div>
                {!r
                  ? <span className="fla-gap-flag fla-gap-flag--pend">Sin responder</span>
                  : isGap
                  ? <span className="fla-gap-flag fla-gap-flag--has">Brecha → genera acción</span>
                  : <span className="fla-gap-flag fla-gap-flag--ok">✓ Sin brecha</span>
                }
              </div>
              <div className="fla-bm-body">
                <div>
                  <label className="fla-rate-lbl">Calificación de cumplimiento</label>
                  <div className="fla-rate-grid">
                    {[
                      ["compliant","sel-c","✓","Cumple","Compliant"],
                      ["nc","sel-nc","✕","No cumple","Non-Compliant"],
                      ["ue","sel-ue","?","Sin corroborar","Uncorroborated"],
                      ["na","sel-na","—","No aplica","N/A"],
                    ].map(([val, cls, ico, es, en]) => (
                      <button
                        key={val}
                        className={`fla-rate-btn${r === val ? ` fla-rate-btn--${cls}` : ""}`}
                        onClick={() => setRating(b.c, val)}
                      >
                        <span className="fla-rate-ico">{ico}</span>
                        {es}<small>{en}</small>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="fla-fld">
                  <label>Evidencia / observaciones de soporte</label>
                  <textarea
                    value={notes[b.c] || ""}
                    placeholder="Describí la evidencia: documentos, registros, hallazgos en planta, fecha…"
                    onChange={e => setNote(b.c, e.target.value)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── VIEW: GAP ────────────────────────────────────────────────────────────

  function ViewGap() {
    const nc = gaps.filter(g => g.status === "nc").length;
    const ue = gaps.filter(g => g.status === "ue").length;
    const elems = new Set(gaps.map(g => g.elem.id)).size;
    return (
      <div>
        <div className="fla-ph">
          <div>
            <h1 className="fla-ph-title">Brechas</h1>
            <div className="fla-ph-sub">Lo que quedó marcado como No cumple o Sin corroborar. Se arma solo.</div>
          </div>
          <div className="fla-ph-r">
            <button className="fla-btn fla-btn--pr fla-btn--sm" onClick={() => goStage("cap")}>Ir al Plan de Acción →</button>
          </div>
        </div>
        <FlaStepper />
        <div className="fla-kpi-row">
          <div className="fla-kpi fla-kpi--crit"><div className="fla-kpi-lbl">Brechas totales</div><div className="fla-kpi-vl">{gaps.length}</div><div className="fla-kpi-frml">NC + UE</div></div>
          <div className="fla-kpi fla-kpi--r"><div className="fla-kpi-lbl">No cumple</div><div className="fla-kpi-vl">{nc}</div><div className="fla-kpi-frml">Non-Compliant</div></div>
          <div className="fla-kpi fla-kpi--y"><div className="fla-kpi-lbl">Sin corroborar</div><div className="fla-kpi-vl">{ue}</div><div className="fla-kpi-frml">Uncorroborated</div></div>
          <div className="fla-kpi fla-kpi--o"><div className="fla-kpi-lbl">Elementos afectados</div><div className="fla-kpi-vl">{elems}/9</div><div className="fla-kpi-frml">del Código</div></div>
        </div>
        <div className="fla-listcard">
          {gaps.length === 0 ? (
            <div className="fla-empty"><span className="fla-empty-ico">✓</span>No hay brechas registradas. Todo criterio marcado <b>No cumple</b> o <b>Sin corroborar</b> aparecerá aquí.</div>
          ) : (
            <table className="fla-tbl">
              <thead>
                <tr>
                  <th>Cód.</th><th>Elemento</th><th>Benchmark / brecha</th><th>Estado</th><th>Evidencia</th>
                </tr>
              </thead>
              <tbody>
                {gaps.map(g => (
                  <tr key={g.code}>
                    <td className="fla-mono">{g.code}</td>
                    <td><b>{g.elem.ico} {g.elem.codeStd}</b> · {g.elem.name.split("(")[0].trim()}</td>
                    <td>{g.title}<div className="fla-tbl-sub">{g.bench}</div></td>
                    <td><Pill status={g.status} /></td>
                    <td>{g.note || <span className="fla-soft">— sin evidencia —</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DDNextBar title="Paso 3 · Plan de Acción" text="Definí una acción correctiva para cada brecha." button="Ir al Plan de Acción" to="/admin/fla-assessment?v=cap" />
      </div>
    );
  }

  // ── VIEW: CAP ────────────────────────────────────────────────────────────

  function ViewCAP() {
    const open   = Object.values(capdata).filter(c => c.status === "Abierta").length;
    const prog   = Object.values(capdata).filter(c => c.status === "En progreso").length;
    const closed = Object.values(capdata).filter(c => c.status === "Cerrada" || c.status === "Verificada").length;
    return (
      <div>
        <div className="fla-ph">
          <div>
            <h1 className="fla-ph-title">Plan de Acción</h1>
            <div className="fla-ph-sub">Una línea de acción por cada brecha. Definí acción, responsable, plazo y estado de remediación.</div>
          </div>
          <div className="fla-ph-r">
            <button className="fla-btn fla-btn--sm" onClick={syncCAP}>↻ Sincronizar con brechas</button>
          </div>
        </div>
        <FlaStepper />
        <div className="fla-kpi-row">
          <div className="fla-kpi fla-kpi--b"><div className="fla-kpi-lbl">Acciones totales</div><div className="fla-kpi-vl">{gaps.length}</div><div className="fla-kpi-frml">1 por brecha</div></div>
          <div className="fla-kpi fla-kpi--r"><div className="fla-kpi-lbl">Abiertas</div><div className="fla-kpi-vl">{open}</div><div className="fla-kpi-frml">sin iniciar</div></div>
          <div className="fla-kpi fla-kpi--o"><div className="fla-kpi-lbl">En progreso</div><div className="fla-kpi-vl">{prog}</div><div className="fla-kpi-frml">en remediación</div></div>
          <div className="fla-kpi fla-kpi--g"><div className="fla-kpi-lbl">Cerradas / verif.</div><div className="fla-kpi-vl">{closed}</div><div className="fla-kpi-frml">remediadas</div></div>
        </div>
        <div className="fla-note fla-note--warn">
          <span className="fla-note-ic">!</span>
          <div>El CAP se genera a partir de las brechas del Gap Analysis. Si modificás una respuesta en la Autoevaluación, volvé acá y presioná <b>Sincronizar con brechas</b>.</div>
        </div>
        <div className="fla-listcard">
          {gaps.length === 0 ? (
            <div className="fla-empty"><span className="fla-empty-ico">🎯</span>No hay acciones pendientes. Usá <b>Sincronizar</b> después de registrar brechas en la Autoevaluación.</div>
          ) : (
            gaps.map(g => {
              const c = capdata[g.code] || { root: "", action: "", owner: "", due: "", status: "Abierta" };
              const stPill = c.status === "Abierta" ? "r" : c.status === "En progreso" ? "o" : c.status === "Cerrada" ? "g" : "b";
              const isOpen = openCaps[g.code];
              return (
                <div key={g.code} className={`fla-cap-card${isOpen ? " fla-cap-card--open" : ""}`}>
                  <div className="fla-cap-h" onClick={() => toggleCap(g.code)}>
                    <span className="fla-cap-code">{g.code}</span>
                    <span className="fla-cap-ti">{g.elem.ico} {g.title}</span>
                    <Pill status={g.status} />
                    <span className={`fla-pill fla-pill--${stPill}`}>{c.status}</span>
                    <span className="fla-cap-arrow">{isOpen ? "▲" : "▼"}</span>
                  </div>
                  {isOpen && (
                    <div className="fla-cap-body">
                      <div className="fla-fld fla-fld--full">
                        <label>Causa raíz (¿por qué ocurre?)</label>
                        <textarea value={c.root} placeholder="Análisis de causa raíz…" onChange={e => updateCap(g.code, "root", e.target.value)} />
                      </div>
                      <div className="fla-fld fla-fld--full">
                        <label>Acción correctiva</label>
                        <textarea value={c.action} placeholder="Qué se hará para cerrar la brecha…" onChange={e => updateCap(g.code, "action", e.target.value)} />
                      </div>
                      <div className="fla-fld">
                        <label>Responsable</label>
                        <input type="text" value={c.owner} placeholder="Nombre / área" onChange={e => updateCap(g.code, "owner", e.target.value)} />
                      </div>
                      <div className="fla-fld">
                        <label>Fecha límite</label>
                        <input type="date" value={c.due} onChange={e => updateCap(g.code, "due", e.target.value)} />
                      </div>
                      <div className="fla-fld">
                        <label>Estado</label>
                        <select value={c.status} onChange={e => updateCap(g.code, "status", e.target.value)}>
                          {FLA_CAP_STATUS.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ── VIEW: REPORTE ────────────────────────────────────────────────────────

  function ViewReport() {
    const g = globalScore;
    const closed = Object.values(capdata).filter(c => c.status === "Cerrada" || c.status === "Verificada").length;
    const tot = FLA_TOTAL_BM;
    const pctCls = g.pct >= 90 ? "g" : g.pct >= 70 ? "y" : g.pct >= 50 ? "o" : "r";
    return (
      <div>
        <div className="fla-ph no-print">
          <div>
            <h1 className="fla-ph-title">Reporte</h1>
            <div className="fla-ph-sub">Resumen de cumplimiento. Imprimí o exportá para compartir.</div>
          </div>
          <div className="fla-ph-r">
            <button className="fla-btn fla-btn--sm" onClick={() => flaExportJSON(db)}>⬇ Exportar datos (JSON)</button>
            <button className="fla-btn fla-btn--sm" onClick={triggerImport}>⬆ Importar</button>
            <button className="fla-btn fla-btn--pr fla-btn--sm" onClick={() => window.print()}>🖨 Imprimir / PDF</button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />
          </div>
        </div>
        <FlaStepper />

        <div className="fla-print-only fla-print-hd">
          <div className="fla-print-hd-row"><span className="fla-print-hd-mark">✦</span><div><b>Sustemia</b> · Reporte de Autoevaluación FLA Workplace Code</div></div>
        </div>

        {/* Encabezado fábrica */}
        <div className="fla-report-header">
          <b>Fábrica:</b> {setup.name || "—"} &nbsp;|&nbsp; <b>País:</b> {setup.country || "—"}{setup.city ? ` · ${setup.city}` : ""} &nbsp;|&nbsp; <b>Marca(s):</b> {setup.brand || "—"}<br />
          <b>Trabajadores:</b> {setup.workers || "—"} &nbsp;|&nbsp; <b>Producto:</b> {setup.product || "—"} &nbsp;|&nbsp; <b>Período:</b> {setup.period || "—"}<br />
          <b>Responsable:</b> {setup.resp || "—"} &nbsp;|&nbsp; <b>Fecha:</b> {setup.date || "—"}
        </div>

        {/* KPIs */}
        <div className="fla-kpi-row">
          <div className={`fla-kpi fla-kpi--${pctCls}`}><div className="fla-kpi-lbl">Cumplimiento global</div><div className="fla-kpi-vl">{g.applicable > 0 ? `${g.pct}%` : "—"}</div><div className="fla-kpi-frml">Compliant ÷ aplicables</div></div>
          <div className="fla-kpi fla-kpi--b"><div className="fla-kpi-lbl">Benchmarks evaluados</div><div className="fla-kpi-vl">{g.answered}/{FLA_TOTAL_BM}</div><div className="fla-kpi-frml">cobertura</div></div>
          <div className="fla-kpi fla-kpi--crit"><div className="fla-kpi-lbl">Brechas abiertas</div><div className="fla-kpi-vl">{gaps.length}</div><div className="fla-kpi-frml">NC + UE</div></div>
          <div className="fla-kpi fla-kpi--g"><div className="fla-kpi-lbl">Acciones cerradas</div><div className="fla-kpi-vl">{closed}/{gaps.length || 0}</div><div className="fla-kpi-frml">remediación</div></div>
        </div>

        {/* Barra global */}
        <div className="fla-listcard">
          <div className="fla-listcard-hd"><h3>Cumplimiento global del Código</h3><span className="fla-card-meta">Índice de cumplimiento = Compliant ÷ (benchmarks aplicables). Los N/A se excluyen del denominador.</span></div>
          <div style={{ padding: "4px 2px" }}>
            <div className="fla-prog-bar">
              {g.c  > 0 && <div className="fla-seg fla-seg--c"  style={{ width: `${(g.c  / tot) * 100}%` }} />}
              {g.nc > 0 && <div className="fla-seg fla-seg--nc" style={{ width: `${(g.nc / tot) * 100}%` }} />}
              {g.ue > 0 && <div className="fla-seg fla-seg--ue" style={{ width: `${(g.ue / tot) * 100}%` }} />}
              {g.na > 0 && <div className="fla-seg fla-seg--na" style={{ width: `${(g.na / tot) * 100}%` }} />}
            </div>
            <div className="fla-prog-legend">
              <span><i className="fla-dot fla-dot--g" /> Compliant</span>
              <span><i className="fla-dot fla-dot--r" /> Non-Compliant</span>
              <span><i className="fla-dot fla-dot--y" /> Uncorroborated</span>
              <span><i className="fla-dot fla-dot--muted" /> N/A</span>
            </div>
          </div>
        </div>

        {/* Grid por elemento */}
        <div className="fla-listcard">
          <div className="fla-listcard-hd"><h3>Scoring por elemento del Código</h3><span className="fla-card-meta">Porcentaje de benchmarks Compliant sobre aplicables, por cada uno de los 9 elementos.</span></div>
          <ElemGrid card onNavigate={() => goStage("assess")} />
        </div>

        {/* Tabla resumen brecha → acción */}
        <div className="fla-listcard" style={{ padding: 0, overflow: "hidden" }}>
          <div className="fla-listcard-hd" style={{ padding: "16px 18px" }}>
            <h3>Resumen de brechas y acciones</h3>
            <span className="fla-card-meta">listado consolidado para seguimiento</span>
          </div>
          {gaps.length === 0 ? (
            <div className="fla-empty"><span className="fla-empty-ico">✓</span>Sin brechas registradas.</div>
          ) : (
            <table className="fla-tbl">
              <thead>
                <tr><th>Cód.</th><th>Elem.</th><th>Brecha</th><th>Estado</th><th>Acción correctiva</th><th>Resp.</th><th>Plazo</th><th>CAP</th></tr>
              </thead>
              <tbody>
                {gaps.map(g => {
                  const c = capdata[g.code] || {};
                  return (
                    <tr key={g.code}>
                      <td className="fla-mono">{g.code}</td>
                      <td>{g.elem.ico} {g.elem.codeStd}</td>
                      <td>{g.title}</td>
                      <td><Pill status={g.status} /></td>
                      <td>{c.action || <span className="fla-soft">— pendiente —</span>}</td>
                      <td>{c.owner || "—"}</td>
                      <td>{c.due || "—"}</td>
                      <td>{c.status || "Abierta"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ── VIEW: GUÍA ───────────────────────────────────────────────────────────

  const FLOW_STEPS = [
    ["🏭", "1", "Configurar fábrica", "Cargá los datos de la instalación que se autoevalúa."],
    ["📝", "2", "Autoevaluar", "Calificá cada benchmark y adjuntá evidencia."],
    ["🔍", "3", "Ver brechas", "El sistema consolida los incumplimientos."],
    ["🎯", "4", "Plan de acción", "Una acción correctiva por cada brecha."],
    ["📊", "5", "Reporte", "Scoring + PDF para la marca o el verificador."],
  ];

  const G_STEPS = [
    { n: "1", tag: "2 min", title: "Configurar la fábrica", sub: "Pestaña «Inicio»",
      items: [
        <>Usá el <b>selector de fábrica</b> (arriba a la derecha) para elegir una existente o creá una <b>nueva en blanco</b>. Vienen 3 fábricas demo que podés explorar o editar.</>,
        <>Completá los datos: nombre, país, marca(s) cliente, dotación, producto, responsable y período. Aparecen en el encabezado del reporte.</>,
        <>Revisá las 9 tarjetas de elementos: cada una muestra el avance y el scoring en vivo de esa fábrica.</>,
      ],
      tip: <>Todo se guarda automáticamente. Podés tener varias fábricas cargadas y moverte entre ellas sin perder nada.</>, tipIco: "✓" },
    { n: "2", tag: "núcleo", title: "Hacer la autoevaluación", sub: "Pestaña «Autoevaluación» · el paso central",
      items: [
        <>Las pestañas de colores son los <b>9 elementos del Código</b> (ER, ND, H/A, F, CL, FOA, HSE, HOW, C). Entrá a cada una.</>,
        <>Dentro de cada elemento vas a ver sus <b>benchmarks</b>: cada uno tiene un código (ej. <code>HSE.6</code>) y el requisito que la FLA verificaría.</>,
        <>Para cada benchmark elegí <b>una calificación</b> (ver el cuadro de abajo) y escribí la <b>evidencia / observación</b> que respalda esa decisión.</>,
        <>El punto de color en cada pestaña te indica el estado del elemento: verde (todo ok), amarillo (incompleto), naranja (hay dudas), rojo (hay incumplimientos).</>,
      ],
      tip: <>Sé honesto al calificar: esta herramienta sirve para <b>prepararte</b>. Una brecha detectada acá es una brecha que evitás en la verificación real.</>, tipIco: "★" },
  ];

  const G_STEPS_2 = [
    { n: "3", tag: "auto", title: "Revisar el Gap Analysis", sub: "Pestaña «Gap Analysis» · automático",
      items: [
        <>Todo benchmark marcado <b>Non-Compliant</b> o <b>Uncorroborated</b> aparece acá como una <b>brecha</b>, sin que tengas que cargar nada.</>,
        <>Arriba ves los indicadores: brechas totales, cuántas son incumplimientos, cuántas son falta de evidencia, y cuántos de los 9 elementos están afectados.</>,
        <>La tabla lista cada brecha con su código, elemento, descripción, estado y la evidencia que cargaste.</>,
      ] },
    { n: "4", tag: "remediación", title: "Armar el Plan de Acción (CAP)", sub: "Pestaña «Plan de Acción»",
      items: [
        <>El sistema genera <b>una línea de acción por cada brecha</b>. Hacé clic en cada tarjeta para desplegarla.</>,
        <>Completá: <b>causa raíz</b> (por qué ocurre), <b>acción correctiva</b> (qué vas a hacer), <b>responsable</b>, <b>fecha límite</b> y <b>estado</b> (Abierta → En progreso → Cerrada → Verificada).</>,
        <>Si cambiás una respuesta en la Autoevaluación, volvé acá y presioná <b>«Sincronizar con brechas»</b> para actualizar el plan.</>,
      ],
      tip: <>La <b>causa raíz</b> es lo que más valora la FLA: no alcanza con tapar el síntoma, hay que resolver por qué pasó para que no se repita.</>, tipIco: "★" },
    { n: "5", tag: "entregable", title: "Generar el Reporte", sub: "Pestaña «Reporte»",
      items: [
        <>Mirá el <b>dashboard de scoring</b>: cumplimiento global, cobertura, brechas abiertas y acciones cerradas, más el detalle por cada uno de los 9 elementos.</>,
        <>Usá <b>«Imprimir / PDF»</b> para generar el reporte con el encabezado de la fábrica, listo para compartir con la marca o el verificador.</>,
        <>Con <b>«Exportar datos (JSON)»</b> guardás un respaldo o se lo pasás a otro equipo; con <b>«Importar»</b> lo recuperás.</>,
      ] },
  ];

  const STATUS_KEY = [
    ["c", "✓", "Compliant — Cumple", "Hay evidencia clara de que el requisito se cumple plenamente. No genera brecha."],
    ["nc", "✕", "Non-Compliant — No cumple", "Se identificó un incumplimiento concreto del requisito. Genera una brecha y una acción correctiva."],
    ["ue", "?", "Uncorroborated — Sin corroborar", "No hay evidencia suficiente para confirmar el cumplimiento. Genera brecha: hay que conseguir/documentar la evidencia."],
    ["na", "—", "N/A — No aplica", "El requisito no aplica a esta operación. Se excluye del cálculo de cumplimiento."],
  ];

  const FAQS = [
    ["¿Esto certifica a mi fábrica ante la FLA?", "No. Es una herramienta de autodiagnóstico para prepararte. La verificación y la acreditación las hace únicamente la FLA a través de sus assessments independientes."],
    ["¿Qué hago si no tengo la evidencia de un benchmark?", "Marcalo como «Uncorroborated» (sin corroborar). Se convertirá en una brecha cuya acción correctiva es justamente conseguir y documentar esa evidencia."],
    ["¿Pierdo mis datos si cierro el navegador?", "No. Todo se guarda automáticamente. Para mayor seguridad, exportá un respaldo en JSON desde la pestaña Reporte."],
    ["¿Puedo evaluar varias fábricas?", "Sí. Usá el selector de fábrica arriba a la derecha para crear y alternar entre varias instalaciones; cada una tiene su propia autoevaluación, brechas y plan de acción."],
  ];

  function GStepCard({ s }) {
    return (
      <div className="fla-gstep">
        <div className="fla-gstep-h">
          <div className="fla-gstep-n">{s.n}</div>
          <div className="fla-gstep-t"><h3>{s.title}</h3><div className="fla-gstep-s">{s.sub}</div></div>
          {s.tag && <span className="fla-gstep-tag">{s.tag}</span>}
        </div>
        <div className="fla-gstep-b">
          <ol>
            {s.items.map((it, i) => <li key={i}>{it}</li>)}
          </ol>
          {s.tip && (
            <div className="fla-gtip"><span className="fla-gtip-ic">{s.tipIco}</span><div>{s.tip}</div></div>
          )}
        </div>
      </div>
    );
  }

  function ViewGuide() {
    return (
      <div>
        <div className="fla-ph">
          <div>
            <h1 className="fla-ph-title">Guía de uso · cómo hacer la autoevaluación FLA</h1>
            <div className="fla-ph-sub">Paso a paso para que la fábrica complete su autodiagnóstico, identifique brechas y arme su plan de acción.</div>
          </div>
          <div className="fla-ph-r">
            <button className="fla-btn fla-btn--pr fla-btn--sm" onClick={() => goStage("setup")}>Empezar ahora →</button>
          </div>
        </div>
        <FlaStepper />

        <div className="fla-guide-hero">
          <h2>¿Qué es esta herramienta?</h2>
          <p>Es un autodiagnóstico que replica la lógica de verificación de la <b>Fair Labor Association (FLA)</b>. La fábrica se evalúa a sí misma contra los <b>9 elementos del Workplace Code of Conduct</b> y sus <b>Compliance Benchmarks</b> (los requisitos que un verificador FLA revisaría en planta). El resultado: un mapa de brechas y un plan de acción correctiva listo para trabajar.</p>
          <div className="fla-guide-hero-chips">
            <span className="fla-guide-hero-chip">9 elementos del Código</span>
            <span className="fla-guide-hero-chip">47 benchmarks verificables</span>
            <span className="fla-guide-hero-chip">Calificación tipo FLA</span>
            <span className="fla-guide-hero-chip">Gap + CAP + Reporte</span>
          </div>
        </div>

        <div className="fla-flow">
          {FLOW_STEPS.map(([ico, n, title, desc]) => (
            <div key={n} className="fla-flow-step">
              <span className="fla-flow-step-ico">{ico}</span>
              <div className="fla-flow-step-n">{n}</div>
              <h4>{title}</h4>
              <p>{desc}</p>
            </div>
          ))}
        </div>

        {G_STEPS.map(s => <GStepCard key={s.n} s={s} />)}

        {/* cuadro de calificaciones */}
        <div className="fla-gstep">
          <div className="fla-gstep-h">
            <div className="fla-gstep-n">★</div>
            <div className="fla-gstep-t"><h3>Cómo calificar cada benchmark</h3><div className="fla-gstep-s">Esquema de hallazgos que usa la FLA en sus assessments</div></div>
          </div>
          <div className="fla-gstep-b">
            <div className="fla-status-key">
              {STATUS_KEY.map(([cls, ico, title, desc]) => (
                <div key={cls} className="fla-sk-item">
                  <div className={`fla-sk-b fla-sk-b--${cls}`}>{ico}</div>
                  <div className="fla-sk-t"><b>{title}</b><span>{desc}</span></div>
                </div>
              ))}
            </div>
            <div className="fla-gtip"><span className="fla-gtip-ic">i</span><div>El <b>índice de cumplimiento</b> = Compliant ÷ (benchmarks aplicables). Los <b>N/A no cuentan</b> en el denominador, para no castigar a la fábrica por requisitos que no le corresponden.</div></div>
          </div>
        </div>

        {G_STEPS_2.map(s => <GStepCard key={s.n} s={s} />)}

        <MethPanel id="guide" title="📘 La metodología FLA detrás de la herramienta" open={guidePanelOpen} onToggle={() => setGuidePanelOpen(o => !o)}>
          <p>La <b>Fair Labor Association</b> verifica el cumplimiento examinando la adherencia a los <i>Compliance Benchmarks</i> y a los <i>Principios de Monitoreo</i>. Los benchmarks definen los requisitos específicos de cada uno de los 9 elementos del Código; la FLA espera que las fábricas <b>mejoren cuando un estándar no se cumple</b> y desarrollen mecanismos sostenibles para asegurar el cumplimiento continuo.</p>
          <div className="fla-mp-source">El Código define estándares laborales que buscan lograr condiciones de trabajo dignas y humanas, basados en los convenios de la OIT y en buenas prácticas internacionalmente aceptadas. Cada elemento se acompaña de benchmarks que identifican los requisitos específicos para cumplirlo.
            <span className="fla-mp-source-src">— FLA Workplace Code of Conduct and Compliance Benchmarks (rev. 28-10-2020) · fairlabor.org</span>
          </div>
          <h5>El ciclo: autoevaluar → remediar</h5>
          <ol className="fla-mp-steps">
            <li><b>Autoevaluación contra benchmarks:</b> la fábrica revisa cada requisito y reúne la evidencia, igual que en un assessment.</li>
            <li><b>Identificación de brechas:</b> los incumplimientos y la falta de evidencia se registran como hallazgos.</li>
            <li><b>Plan de acción correctiva (CAP):</b> por cada hallazgo se define causa raíz, acción, responsable y plazo.</li>
            <li><b>Seguimiento y mejora continua:</b> se cierran las acciones y se vuelve a evaluar en el siguiente ciclo.</li>
          </ol>
          <div className="fla-mp-source fla-mp-source--warn">Esta es una herramienta de <b>autodiagnóstico y preparación</b>. No sustituye la verificación independiente acreditada de la FLA ni constituye certificación alguna.
            <span className="fla-mp-source-src">— Nota de alcance · Sustemia</span>
          </div>
        </MethPanel>

        <h3 className="fla-faq-title">Preguntas frecuentes</h3>
        {FAQS.map(([q, a], i) => (
          <div key={i} className={`fla-faq${faqOpen[i] ? " fla-faq--open" : ""}`}>
            <div className="fla-faq-q" onClick={() => toggleFaq(i)}>{q}<span className="fla-faq-c">▾</span></div>
            <div className="fla-faq-a">{a}</div>
          </div>
        ))}
      </div>
    );
  }

  // ── Render principal ─────────────────────────────────────────────────────

  return (
    <div className="fla-page">
      <DDNav />

      <div className="fla-content">
        {view === "setup"  && <ViewSetup />}
        {view === "guide"  && <ViewGuide />}
        {view === "assess" && <ViewAssess />}
        {view === "gap"    && <ViewGap />}
        {view === "cap"    && <ViewCAP />}
        {view === "report" && <ViewReport />}
        {view === "report" && (
          <div className="no-print">
            <DDReportCard
              variant="fla"
              title="Informe completo del proceso FLA · descargable · ES / EN / PT"
              previewTitle="Vista previa del informe FLA"
              desc="Documento autocontenido que documenta y explica todo el proceso de la fábrica activa: metodología del FLA Workplace Code, calificación de los 47 benchmarks con su evidencia, brechas, plan de acción correctiva, conclusiones y próximos pasos. Se descarga en HTML y se imprime a PDF desde el propio informe."
              note={globalScore.answered < FLA_TOTAL_BM && (
                <>La autoevaluación aún tiene <b>{FLA_TOTAL_BM - globalScore.answered}</b> benchmarks sin responder (cobertura <b>{globalScore.answered}/{FLA_TOTAL_BM}</b>). El informe los mostrará como «Sin responder»; podés generarlo igual o completar la evaluación primero.</>
              )}
              features={[
                ["1–2 · Resumen + metodología", "Qué es el Código FLA, esquema de calificación, fórmulas, proceso y nota de alcance."],
                ["3–4 · Resultados", "Cumplimiento global y detalle de los 9 elementos: 47 benchmarks con calificación y evidencia."],
                ["5–6 · Brechas + CAP", "Gap Analysis y plan de acción completo: causa raíz, acción, responsable, plazo y estado."],
                ["7–8 · Conclusiones", "Prioridades según los datos, próximos pasos y ficha del documento con fuentes."],
              ]}
              buildHtml={(lang) => buildFlaReport(lang, cur)}
              fileName={(lang) => flaReportFileName(lang, cur)}
            />
          </div>
        )}
      </div>
      <ConfirmModal
        open={confirm.open}
        message={confirm.msg}
        danger
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ open: false, msg: "", onConfirm: null })}
      />

      {facMenuOpen && <div className="fla-fac-overlay" onClick={() => setFacMenuOpen(false)} />}
    </div>
  );
}
